import { AlertTriangle, Loader2, Mail, MailCheck, MessageSquareOff, Sparkles, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { SITE_CONTACT } from "@/config/site-contact";
import { authApi } from "@/features/auth/api";
import { highestSeverity, systemAlerts, type SystemAlert } from "@/features/auth/system-alerts";
import { useAuthStore } from "@/features/auth/store";
import { apiErrorMessage } from "@/lib/api/errors";
import { cn } from "@/lib/utils";

/** Masquage par session : la clé encode l'état et la gravité — si la
 *  situation s'aggrave (essai qui touche à sa fin, licence en période de grâce,
 *  SMS coupé), la clé change et l'alerte réapparaît. */
function useDismissed(): [(key: string) => boolean, (key: string) => void] {
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const isDismissed = (key: string) => {
    if (dismissed.has(key)) return true;
    try {
      return sessionStorage.getItem(`mada.alert.${key}`) === "1";
    } catch {
      return false;
    }
  };
  const dismiss = (key: string) => {
    try {
      sessionStorage.setItem(`mada.alert.${key}`, "1");
    } catch {
      /* stockage indisponible */
    }
    setDismissed((prev) => new Set(prev).add(key));
  };
  return [isDismissed, dismiss];
}

/** Bouton « Renvoyer l'e-mail » du bandeau adresse non vérifiée. */
function ResendVerificationAction() {
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const resend = async () => {
    setPending(true);
    try {
      const r = await authApi.resendEmailVerification();
      setSent(true);
      toast.success(
        r.code === "already_verified"
          ? "Adresse déjà vérifiée."
          : "E-mail de vérification envoyé. Ouvrez le lien reçu.",
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "L'e-mail n'a pas pu être envoyé."));
    } finally {
      setPending(false);
    }
  };
  if (sent) return <span className="shrink-0 opacity-80">E-mail envoyé.</span>;
  return (
    <button
      type="button"
      onClick={resend}
      disabled={pending}
      className="inline-flex shrink-0 items-center gap-1 underline underline-offset-2 hover:no-underline disabled:opacity-60"
    >
      {pending ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> : <Mail className="h-3 w-3" aria-hidden />}
      Renvoyer l'e-mail
    </button>
  );
}

interface AlertItemProps {
  icon: ReactNode;
  message: string;
  action: ReactNode;
  onDismiss: () => void;
}

function AlertItem({ icon, message, action, onDismiss }: AlertItemProps) {
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {icon}
      <span className="min-w-0">{message}</span>
      {action}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Masquer cette alerte"
        className="ml-0.5 rounded p-0.5 opacity-70 transition-opacity hover:bg-black/15 hover:opacity-100"
      >
        <X className="h-3 w-3" aria-hidden />
      </button>
    </span>
  );
}

/**
 * Barre d'alertes système UNIQUE (adresse e-mail, licence, SMS) : une seule
 * ligne fine au lieu de bandeaux empilés. Chaque alerte est masquable pour la
 * session ; elle réapparaît à la session suivante ou si la situation s'aggrave
 * (gravité dans la clé de masquage). Ce qu'il faut dire et sa gravité viennent
 * de `systemAlerts` ; le blocage effectif (licence expirée) reste géré par
 * l'écran dédié du ProtectedLayout.
 */
export function SystemAlertsBar() {
  const me = useAuthStore((s) => s.me);
  const isEditor = me?.is_platform_staff ?? false;
  const alerts = systemAlerts(me);
  const [isDismissed, dismiss] = useDismissed();
  const visible = alerts.filter((a) => !isDismissed(a.key));
  const severity = highestSeverity(visible);
  if (!severity) return null;

  return (
    <div
      role="status"
      className={cn(
        "flex flex-wrap items-center justify-center gap-x-6 gap-y-0.5 px-3 py-1 text-xs font-medium leading-tight",
        severity === "critical" && "bg-warning text-white",
        severity === "warning" && "border-b border-warning/30 bg-warning/10 text-foreground",
        severity === "info" && "border-b border-primary/20 bg-primary/5 text-foreground",
      )}
    >
      {visible.map((a) => (
        <AlertItem
          key={a.key}
          icon={alertIcon(a)}
          message={alertMessage(a)}
          action={alertAction(a, isEditor)}
          onDismiss={() => dismiss(a.key)}
        />
      ))}
    </div>
  );
}

function alertIcon(a: SystemAlert): ReactNode {
  const cls = "h-3.5 w-3.5 shrink-0";
  if (a.kind === "email") return <MailCheck className={cls} aria-hidden />;
  if (a.kind === "sms" && a.disabled) return <MessageSquareOff className={cls} aria-hidden />;
  if (a.kind === "license" && a.severity === "info") return <Sparkles className={cn(cls, "text-primary")} aria-hidden />;
  return <AlertTriangle className={cls} aria-hidden />;
}

function alertMessage(a: SystemAlert): string {
  switch (a.kind) {
    case "email":
      return `Adresse ${a.email} non vérifiée : confirmez-la via le lien reçu.`;
    case "license":
      if (a.inGrace) return `Licence expirée — ${a.daysRemaining} j de grâce avant blocage.`;
      if (a.trial) {
        return a.daysRemaining > 1
          ? `Essai gratuit : ${a.daysRemaining} jours restants.`
          : "Essai gratuit : dernier jour.";
      }
      return `Licence : expire dans ${a.daysRemaining} j.`;
    case "sms":
      return a.disabled
        ? "Service SMS désactivé : aucun envoi."
        : "Solde SMS épuisé : les rappels automatiques ne partent plus.";
  }
}

function alertAction(a: SystemAlert, isEditor: boolean): ReactNode {
  const link = "shrink-0 underline underline-offset-2 hover:no-underline";
  if (a.kind === "email") return <ResendVerificationAction />;
  if (a.kind === "sms") {
    return (
      <Link to="/sms" className={link}>
        {a.disabled ? "Gérer" : "Recharger"}
      </Link>
    );
  }
  if (isEditor) {
    return (
      <Link to="/editeur/licences" className={link}>
        Gérer
      </Link>
    );
  }
  const subject = a.trial ? "Choisir%20une%20formule%20MADA" : "Renouvellement%20de%20licence%20MADA";
  return (
    <a href={`mailto:${SITE_CONTACT.email}?subject=${subject}`} className={cn("inline-flex items-center gap-1", link)}>
      <Mail className="h-3 w-3" aria-hidden />
      {a.trial ? "Choisir une formule" : "Contacter GoldenTech"}
    </a>
  );
}
